import {
  ElementKind,
  Question,
  QuestionTypes,
  WidgetData,
  ElementWithCodeAndValue,
  WidgetType,
} from '@playbooks/core/src/interfaces/ActionCard'
import getHtmlFromMarkdown from '@playbooks/core/src/utils/getHtmlFromMarkdown'

import sanitizedHtml from './sanitizeHtml'
import { useTranslation } from '../contexts/localization/Localization'
import { parseDate } from './utils'

/**
 * Get the result for the given Action Card element.
 * @param element Action Card element with code to provide the result for replacement
 * @param translation Translator function
 * @returns Result as string
 */
export default function getResult(
  element: ElementWithCodeAndValue,
  translation: ReturnType<typeof useTranslation>['text'],
  useBrackets: boolean,
) {
  const isWidget = element.kind === ElementKind.WIDGET
  const placeholder = isWidget ? (element as WidgetData).buttonLabel : (element as Question).text
  const text = useBrackets ? `(${placeholder})` : `${placeholder}`

  const result = (() => {
    switch (element.type) {
      case QuestionTypes.DROPDOWN: {
        const answer = ((element.data || []) as { id: number; name: string }[]).find(item => item.id === element.value)
        return answer?.name || text
      }

      case QuestionTypes.CHECKBOX: {
        return element?.value ? translation('yes') : translation('no')
      }

      case QuestionTypes.LONG_TEXT: {
        return (element?.value as string)?.replaceAll('\n', '<br />') || text
      }

      case QuestionTypes.DATE: {
        return parseDate(element?.value as string)?.toLocaleDateString() ?? text
      }

      case QuestionTypes.DATE_TIME: {
        return parseDate(element?.value as string)?.toLocaleString() ?? text
      }

      case WidgetType.GenericAiPrompt: {
        const value = element.value?.toString?.()

        const html = getHtmlFromMarkdown(value || '')

        return value ? html : text
      }

      default:
        return element.value?.toString?.() || text || element.type
    }
  })()

  return sanitizedHtml(result)
}
